import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, AlertCircle } from 'lucide-react';
import { skipToken } from '@reduxjs/toolkit/query';
import {
  useGetNationalHolisticAnalyticsQuery,
  mergeNationalAnalyticsResponses,
  TermFilter,
  termFilterToQueryValue,
  getClassDisplayName,
  useGetHpcYearsQuery,
} from '../../../services/holisticApi';
import KVSLoader from '../../../../../components/loaders/KVSLoader';

const ParentAnalysis: React.FC = () => {
  const navigate = useNavigate();
  const { data: yearsRes } = useGetHpcYearsQuery();
  const availableYears = useMemo(() => {
    return (yearsRes?.data || [2026, 2025]).map(Number);
  }, [yearsRes]);

  const [academicYear, setAcademicYear] = useState<number>(2025);

  React.useEffect(() => {
    if (yearsRes?.data?.length) {
      const numericYears = yearsRes.data.map(Number);
      if (!numericYears.includes(Number(academicYear))) {
        setAcademicYear(numericYears[0]);
      }
    }
  }, [yearsRes, academicYear]);

  const [term, setTerm] = useState<TermFilter>('overall');
  const isOverall = term === 'overall';

  const singleTermQuery = useGetNationalHolisticAnalyticsQuery(
    !isOverall ? { academicYear, term } : skipToken,
  );
  const term1Query = useGetNationalHolisticAnalyticsQuery(
    isOverall ? { academicYear, term: 1 } : skipToken,
  );
  const term2Query = useGetNationalHolisticAnalyticsQuery(
    isOverall ? { academicYear, term: 2 } : skipToken,
  );

  const effectiveData = useMemo(() => {
    if (!isOverall) return singleTermQuery.data;
    return mergeNationalAnalyticsResponses(term1Query.data, term2Query.data);
  }, [isOverall, singleTermQuery.data, term1Query.data, term2Query.data]);

  const isLoading = isOverall ? (term1Query.isLoading || term2Query.isLoading) : singleTermQuery.isLoading;

  const records = useMemo(() => {
    if (!effectiveData?.data) return [];
    return effectiveData.data.flatMap((region) => region.records || []);
  }, [effectiveData]);



  const stats = useMemo(() => {
    if (!records.length) {
      return {
        highInvolvement: 0,
        moderate: 0,
        lowInvolvement: 0,
        studentsList: [],
      };
    }

    let highInvolvement = 0;
    let lowInvolvement = 0;
    let moderate = 0;

    const list = records.map((record) => {
      // Classify parent support level using socio_emotional_score as an indicator
      const socioScore = Number(record.socio_emotional_score || 0);
      let level: 'High' | 'Moderate' | 'Low';

      if (socioScore >= 70) {
        highInvolvement++;
        level = 'High';
      } else if (socioScore >= 50) {
        moderate++;
        level = 'Moderate';
      } else {
        lowInvolvement++;
        level = 'Low';
      }

      const first = (record.firstName ?? record.first_name ?? '').trim();
      const middle = (record.middleName ?? record.middle_name ?? '').trim();
      const last = (record.lastName ?? record.last_name ?? '').trim();
      const fullName = [first, middle, last].filter(Boolean).join(' ') || `Student ${record.student_id}`;

      return {
        id: record.student_id,
        name: fullName,
        className: getClassDisplayName(Number(record.class_id)),
        section: '-',
        level,
        feedbackDetail: 'Detailed parent feedback is available on the student profile page.',
        regionId: record.region_id,
        schoolId: record.school_id,
        classId: record.class_id,
      };
    });

    return {
      highInvolvement,
      moderate,
      lowInvolvement,
      studentsList: list.slice(0, 50), // Show first 50
    };
  }, [records]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[320px]">
        <KVSLoader />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-[1600px] mx-auto space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-gray-900 mb-1">Parent Feedback Analysis</h1>
          <p className="text-sm text-gray-500">Insights from parent involvement across {records.length} students</p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2">
            <label htmlFor="term" className="text-xs font-medium text-gray-600">Term</label>
            <select
              id="term"
              value={term}
              onChange={(e) => {
                const value = e.target.value;
                setTerm(value === 'overall' ? 'overall' : value === '2' ? 2 : 1);
              }}
              className="text-sm text-gray-700 bg-transparent focus:outline-none"
            >
              <option value="overall">Overall</option>
              <option value="1">Term 1</option>
              <option value="2">Term 2</option>
            </select>
          </div>

          <div className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2">
            <label htmlFor="academicYear" className="text-xs font-medium text-gray-600">Academic Session</label>
            <select
              id="academicYear"
              value={academicYear}
              onChange={(e) => setAcademicYear(Number(e.target.value))}
              className="text-sm text-gray-700 bg-transparent focus:outline-none"
            >
              {availableYears.map((year: number) => (
                <option key={year} value={year}>
                  {year}-{String(Number(year) + 1).slice(-2)}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-emerald-50 rounded-xl border border-emerald-200 p-5 shadow-sm">
          <div className="flex items-center gap-2 mb-2"><CheckCircle size={16} className="text-emerald-600" /><span className="text-xs text-emerald-600 font-semibold">High Involvement</span></div>
          <p className="text-3xl font-bold text-emerald-700">{stats.highInvolvement}</p>
        </div>
        <div className="bg-amber-50 rounded-xl border border-amber-200 p-5 shadow-sm">
          <div className="flex items-center gap-2 mb-2"><AlertCircle size={16} className="text-amber-600" /><span className="text-xs text-amber-600 font-semibold">Moderate</span></div>
          <p className="text-3xl font-bold text-amber-700">{stats.moderate}</p>
        </div>
        <div className="bg-red-50 rounded-xl border border-red-200 p-5 shadow-sm">
          <div className="flex items-center gap-2 mb-2"><AlertCircle size={16} className="text-red-600" /><span className="text-xs text-red-600 font-semibold">Low Engagement</span></div>
          <p className="text-3xl font-bold text-red-700">{stats.lowInvolvement}</p>
        </div>
      </div>

      {/* Student breakdown */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-700">Student-wise Parent Feedback Summary</h3>
          <span className="text-xs text-gray-400">Showing first 50 students</span>
        </div>
        <div className="divide-y divide-gray-100">
          {stats.studentsList.map((student: any) => {
            const isHigh = student.level === 'High';
            const isLow = student.level === 'Low';
            return (
              <div
                key={student.id}
                onClick={() => navigate(`/kvs-samagam/student-analytics/region/${student.regionId}/school/${student.schoolId}/class/${student.classId}/students/${student.id}/parents?year=${academicYear}&term=${termFilterToQueryValue(term)}`)}
                className="px-6 py-4 flex items-center justify-between hover:bg-indigo-50/40 cursor-pointer transition"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-xs font-bold">
                    {student.name.split(' ').map((n: string) => n[0]).join('')}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">{student.name}</p>
                    <p className="text-xs text-gray-400">{student.className}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right hidden sm:block">
                    <p className="text-xs text-gray-500">Support Status</p>
                    <p className="text-xs text-gray-400 max-w-xs truncate">{student.feedbackDetail}</p>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-[10px] font-semibold ${isHigh ? 'bg-emerald-50 text-emerald-700' : isLow ? 'bg-red-50 text-red-700' : 'bg-amber-50 text-amber-700'}`}>
                    {student.level} Support
                  </span>
                </div>
              </div>
            );
          })}
          {stats.studentsList.length === 0 && (
            <div className="px-6 py-8 text-center text-sm text-gray-400">No data available</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ParentAnalysis;
